import { companyName, IS_MAIN_COMPANY } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { useTitle } from 'utils/pageTitle'

import { CredentialConfig } from './CredentialConfig'
import { DataVerifySSA } from './DataVerifySSA'
import { NationalFloodTab } from './NationalFlood'
import { vendorExtraSections } from './vendorExtraSections'

type menuType = {
  [key: string]: string // fix this
}

type menuGroupType = {
  title: string
  items: menuType
}

const leftMenuGroups: menuGroupType[] = [
  {
    title: 'Credit Reporting',
    items: {
      cicMeridianLink: 'Credit Report (MeridianLink CIC)',
      ctiMeridianLink: 'Credit Report (MeridianLink CtiNetwork)',
      xactus: 'Credit Report (Xactus360)',
      Crs: 'Credit Report (CRS)',
    },
  },
  {
    title: 'Borrower & Entity Verification',
    items: {
      DataverifySSA: 'DataVerify SSA',
      dataverifySSA: 'Dataverify SSA Orders',
      DataverifyTAX: 'DataVerify 4506',
      dataverifyTAX: 'Dataverify 4506 Orders',
      DataverifyDrive: 'DataVerify Drive',
      snappt: 'Snappt',
      middesk: 'Middesk',
      lexisnexis: 'SmartLinx Person Report',
    },
  },
  {
    title: 'Appraisal & Valuation',
    items: {
      amcDirect: 'AMC Direct',
      appraisalNation: 'Appraisal Nation',
      arivs: 'Arivs',
      classValuation: 'Class Valuation',
      valutrac: 'Elite Valuation',
      naan: 'Nationwide AMC (NAAN)',
      nationWide: 'Nationwide Appraisal Network (NAN)',
      richerValues: 'Richer Values',
      trinity: 'Trinity',
      vsamc: 'Valuation Services AMC (VSAMC)',
      visionVMC: 'Vision VMC',
    },
  },
  {
    title: 'Property Data & Inspection',
    items: {
      Inspectify: 'Inspectify',
      Sitewire: 'Sitewire',
      sitexPro: 'SitexPro',
    },
  },
  {
    title: 'Title & Settlement',
    items: {
      afx: 'Afx Research',
      serviceLinkWTG: 'WTG',
    },
  },
  {
    title: 'Flood Determination',
    items: {
      DataverifyFlood: 'DataVerify Flood',
      nationalFlood: 'National Flood',
      XactusFlood: 'Flood (Xactus)',
      serviceLink: 'Service Link Flood',
    },
  },
  {
    title: 'Insurance',
    items: {
      obie: 'Obie',
    },
  },
  {
    title: 'Loan Documents & E-Signature',
    items: {
      automateLoanDocs: 'Automate Loan Docs',
      docMagic: 'DocMagic',
      docgen: 'DocGen (e-signature)',
      goDocs: 'GoDocs',
      lightningDocs: 'Lightning Docs',
    },
  },
  {
    title: 'Payments & Banking',
    items: {
      plaid: 'Plaid',
      stripe: 'Stripe',
      usio: 'USIO',
    },
  },
  {
    title: 'Pricing & Capital Markets',
    items: {
      rtltrader: 'RTL Trader',
      setpoint: 'Setpoint',
    },
  },
  {
    title: 'Origination & Servicing Systems',
    items: {
      bytepro: 'Bytepro',
      fci: 'FCI',
      Nexys: 'Nexys',
    },
  },
  {
    title: 'CRM & Marketing',
    items: {
      Close: 'Close',
      gtm: 'Google Tag Manager',
      hubspot: 'Hubspot',
      salesforce: 'Salesforce',
    },
  },
  {
    title: 'Address & Mail',
    items: {
      usps: 'USPS',
      lob: 'Lob',
    },
  },
  {
    title: 'Tax Filing',
    items: {
      taxbandits: 'TaxBandits',
    },
  },
]

const leftMenuItems: menuType = leftMenuGroups.reduce(
  (all: menuType, group: menuGroupType) => ({ ...all, ...group.items }),
  {},
)

const defaultMenu = Object.keys(leftMenuItems)[0]

export * from './NationalFlood/NationalFlood'

export default function Vendors() {
  useTitle(`Vendors - ${companyName}`)

  const { auth } = useSelector((state: any) => ({
    auth: state.auth,
  }))
  const isMaster = auth.profile.master
  const navigate = useHistory()
  const { menu: menuParam } = useParams<{ menu?: string }>()
  const initialMenu = menuParam && leftMenuItems[menuParam] ? menuParam : defaultMenu
  const [selectedMenu, setSelectedMenu] = useState(initialMenu)

  const setMenu = (menuItem: string) => {
    setSelectedMenu(menuItem)
    navigate.push(`/vendors/${menuItem}`)
  }

  const visibleGroups = useMemo(
    () =>
      leftMenuGroups
        .map((group: menuGroupType) => ({
          title: group.title,
          keys: Object.keys(group.items).filter(
            (item: string) => IS_MAIN_COMPANY || isMaster || !['usps'].includes(item),
          ),
        }))
        .filter((group) => group.keys.length > 0),
    [isMaster],
  )

  const renderFragment = useMemo(() => {
    switch (selectedMenu) {
      case 'dataverifySSA':
        return <DataVerifySSA type="ssa" key="ssa" />
      case 'dataverifyTAX':
        return <DataVerifySSA type="tax" key="tax" />
      case 'nationalFlood':
        return <NationalFloodTab />
      default:
        return (
          <CredentialConfig
            type={selectedMenu}
            title={leftMenuItems[selectedMenu]}
            key={selectedMenu}
            sectionTitle={selectedMenu === 'Nexys' ? 'Push' : undefined}
          />
        )
    }
  }, [selectedMenu])

  const extraSections = vendorExtraSections[selectedMenu] || []

  useEffect(() => {
    if (menuParam && leftMenuItems[menuParam]) {
      setSelectedMenu(menuParam)
    } else if (!menuParam) {
      setMenu(defaultMenu)
    }
  }, [menuParam])

  return (
    <div className="vendors-container py-6">
      <div className="vendors-wrapper max-w-screen-2xl m-auto grid grid-cols-12 gap-6 px-2">
        <div className="sidebar-left col-span-12 md:col-span-3 shrink-0 bg-white shadow1 rounded">
          <div className="sidebar-items flex flex-col p-4 pb-20">
            {visibleGroups.map((group) => (
              <div key={group.title} className="mt-5 first:mt-0">
                <p className="text-sm font-semibold uppercase tracking-wide text-shade-blue border-b border-shade-blue/40 pb-1">
                  {group.title}
                </p>
                <ul>
                  {group.keys.map((item: string) => (
                    <li
                      key={item}
                      onClick={() => {
                        setMenu(item)
                      }}
                      className="border-b py-2 last:border-b-0"
                    >
                      <p
                        className={`hover:underline cursor-pointer ${
                          selectedMenu === item ? 'border px-4 py-1 bg-zinc-100' : 'py-1'
                        }`}
                      >
                        {leftMenuItems[item as keyof typeof leftMenuItems]}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="content-right col-span-12 md:col-span-9 bg-white p-4 rounded shadow1">
          {renderFragment}
          {extraSections.map((Section, index) => (
            <Section key={`${selectedMenu}-extra-${index}`} />
          ))}
        </div>
      </div>
    </div>
  )
}
